import React, { useMemo } from "react";
import { type Prize, StockStrategy } from "kadow-jetpack-model";
import "./styles.scss";
import KC_Separator from "../../../designsystem/KC_Separator/KC_Separator";
import { KC_Sizes } from "../../../designsystem/ds_commons";
import { tt } from "../../../utils/i18n/i18n";

interface Props {
  items: Prize[];
  distributionType: StockStrategy;
  lostPc: number;
}

export default function PercentageControl(props: Props) {
  const showLostPc = useMemo(() => {
    return props.lostPc > 0;
  }, [props.lostPc]);

  const itemsPlaceWidthPc = useMemo(() => {
    if (!props.items) {
      return 0;
    }
    const lostItemCount = props.lostPc > 0 ? 1 : 0;

    return Math.trunc(100 / (props.items.length + lostItemCount));
  }, [props.items, props.lostPc]);

  const lostItemWidth = useMemo(() => {
    if (!props.items.length) {
      return "100%";
    }
    const value = 100 - props.items.length * itemsPlaceWidthPc;
    return `${value}%`;
  }, [props.items, itemsPlaceWidthPc]);

  const recomputePc = (prize: Prize) => {
    if (props.distributionType === StockStrategy.None) {
      return Math.trunc(prize.pc);
    }

    const totalPc = props.items.reduce(
      (sum, prize) => sum + (prize.pc || 0),
      0
    );

    return Math.trunc(((prize.pc ?? 0) * 100) / totalPc);
  };

  const displayPcText = (prize: Prize, prizeId: number, pc: number) => {
    if (isNaN(pc)) {
      return tt("gift_with_position", {
        position: prizeId + 1
      });
    }

    if (!prize.name) {
      return tt("gift_with_position_and_percentage", {
        position: prizeId + 1,
        pc
      });
    }

    return tt("gift_with_name_and_percentage", {
      name: prize.name,
      pc
    });
  };

  return (
    <div
      className="w-100"
      style={{
        margin: "0 auto"
      }}
    >
      <div className="pc-control-container">
        {props.items.map((prize, index) => {
          return (
            <div
              key={index}
              className={"position-relative win-pc-item"}
              style={{
                width: `${itemsPlaceWidthPc}%`
              }}
            >
              <span
                className={"position-absolute display-lg"}
                style={{
                  width: "100%",
                  textAlign: "center",
                  textOverflow: "clip",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  whiteSpace: "nowrap"
                }}
              >
                {displayPcText(prize, index, recomputePc(prize))}
              </span>
            </div>
          );
        })}

        {showLostPc && (
          <div
            className={"position-relative border-right-1 lost-pc-item"}
            style={{
              width: lostItemWidth
            }}
          >
            <span
              className={"position-absolute text-left display-lg"}
              style={{
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                whiteSpace: "nowrap"
              }}
            >
              {tt("loss_percentage", { percentage: Math.floor(props.lostPc) })}
            </span>
          </div>
        )}
      </div>
      <KC_Separator size={KC_Sizes.sm} />
      <div className={"pc-legend-container"}>
        {props.items.map((prize, index) => {
          return (
            <div className="d-flex gap-sm pc-legend-item">
              <div key={index} className={"pc-legend-item-color"}></div>
              <div> {displayPcText(prize, index, recomputePc(prize))}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
